import * as React from "react";

import type { TimeFormat } from "../pickers/BasePicker";
import type {
  BaseInputProps,
  DateRelatedProps,
  DisableValuesProps,
  EnableValuesProps,
  MarkedValuesProps,
  MinMaxValueProps,
  MultimodeProps,
  TimeRelatedProps,
} from "./BaseInput";
import DateInput from "./DateInput";
import TimeInput from "./TimeInput";
import { TIME_FORMAT, parseValue } from "./parse";

const DEFAULT_DATE_FORMAT = "dd/MM/yyyy";
const DEFAULT_TIME_FORMAT: TimeFormat = "24";

export interface DateTimeInputProps
  extends
    Omit<BaseInputProps, "icon" | "iconPosition" | "placeholder" | "name">,
    DateRelatedProps,
    TimeRelatedProps,
    MultimodeProps,
    DisableValuesProps,
    EnableValuesProps,
    MarkedValuesProps,
    MinMaxValueProps {
  /** Display mode to start for the date picker. */
  startMode?: "year" | "month" | "day";
  /** Placeholder for the date field. */
  datePlaceholder?: string;
  /** Placeholder for the time field. */
  timePlaceholder?: string;
  /** @deprecated Prefer datePlaceholder. */
  placeholder?: string;
  /** Icon position inside each Input. Default: 'left'. */
  iconPosition?: "right" | "left";
  /** Optional name for the date field. */
  dateName?: string;
  /** Optional name for the time field. */
  timeName?: string;
  /** @deprecated Prefer dateName. */
  name?: string;
  /** Style applied to the wrapper. */
  style?: React.CSSProperties;
  /** Style applied to the date input. */
  dateStyle?: React.CSSProperties;
  /** Style applied to the time input. */
  timeStyle?: React.CSSProperties;
}

export type DateTimeInputOnChangeData = DateTimeInputProps;

type DraftParts = { date: string; time: string };

type FormatProps = {
  dateFormat?: string;
  timeFormat?: TimeFormat;
  localization?: string;
};

function getDateFormat(props: FormatProps): string {
  return props.dateFormat || DEFAULT_DATE_FORMAT;
}

function getTimeFormatString(props: FormatProps): string {
  return TIME_FORMAT[props.timeFormat || DEFAULT_TIME_FORMAT];
}

/** Combined value format: dateFormat + space + time format. */
function getDateTimeFormat(props: FormatProps): string {
  return `${getDateFormat(props)} ${getTimeFormatString(props)}`;
}

function splitValue(
  value: string | Date | undefined,
  props: FormatProps,
): DraftParts {
  if (value === undefined || value === null || value === "") {
    return { date: "", time: "" };
  }
  const parsed = parseValue(
    value,
    getDateTimeFormat(props),
    props.localization,
  );
  if (parsed) {
    return {
      date: parsed.format(getDateFormat(props)),
      time: parsed.format(getTimeFormatString(props)),
    };
  }
  if (typeof value === "string") {
    const idx = value.indexOf(" ");
    if (idx >= 0) {
      return {
        date: value.slice(0, idx).trim(),
        time: value.slice(idx + 1).trim(),
      };
    }
  }
  return { date: "", time: "" };
}

function combineParts(
  date: string,
  time: string,
  props: FormatProps,
): string | null {
  if (!date || !time) {
    return null;
  }
  const combined = `${date} ${time}`;
  const parsed = parseValue(
    combined,
    getDateTimeFormat(props),
    props.localization,
  );
  if (!parsed) {
    return null;
  }
  return parsed.format(getDateTimeFormat(props));
}

/**
 * Side-by-side DateInput + "at" + TimeInput.
 * Emits a combined datetime string only when both parts are valid.
 */
const DateTimeInput = (props: DateTimeInputProps) => {
  const {
    value,
    onChange,
    onClear,
    timeFormat = DEFAULT_TIME_FORMAT,
    datePlaceholder,
    timePlaceholder = "Time",
    placeholder,
    dateName,
    timeName,
    name,
    style,
    dateStyle,
    timeStyle,
    iconPosition = "left",
    clearable,
    closable,
    disabled,
    size,
    fluid,
    localization,
    minDate,
    maxDate,
    disable,
    enable,
    marked,
    markColor,
    startMode,
    initialDate,
    preserveViewMode,
    disableMinute,
    minuteStep,
    className,
  } = props;

  const dateFormat = getDateFormat(props);
  const formatProps: FormatProps = { dateFormat, timeFormat, localization };

  const [drafts, setDrafts] = React.useState<DraftParts>(() =>
    splitValue(value, formatProps),
  );

  // Compare by formatted string so `value={new Date(...)}` each render is stable.
  const resolvedDateTimeFormat = getDateTimeFormat(formatProps);
  let valueKey = "";
  if (value !== undefined && value !== null && value !== "") {
    const parsed = parseValue(value, resolvedDateTimeFormat, localization);
    valueKey = parsed ? parsed.format(resolvedDateTimeFormat) : String(value);
  }

  const prevValueKeyRef = React.useRef(valueKey);

  React.useEffect(() => {
    if (prevValueKeyRef.current !== valueKey) {
      prevValueKeyRef.current = valueKey;
      setDrafts(splitValue(value, { dateFormat, timeFormat, localization }));
    }
  }, [valueKey, value, dateFormat, timeFormat, localization]);

  const notifyClear = (e: React.SyntheticEvent<HTMLElement>) => {
    (onClear || onChange)?.(e, { value: "" });
  };

  const tryEmitCombined = (
    e: React.SyntheticEvent<HTMLElement>,
    nextDate: string,
    nextTime: string,
  ) => {
    if (!nextDate || !nextTime) {
      return;
    }
    const combined = combineParts(nextDate, nextTime, formatProps);
    if (combined !== null) {
      onChange(e, { value: combined });
    }
  };

  const handleDateChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value: nextDate }: { value: string },
  ) => {
    const nextTime = drafts.time;
    setDrafts({ date: nextDate, time: nextTime });
    if (!nextDate) {
      notifyClear(e);
      return;
    }
    tryEmitCombined(e, nextDate, nextTime);
  };

  const handleTimeChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value: nextTime }: { value: string },
  ) => {
    const nextDate = drafts.date;
    setDrafts({ date: nextDate, time: nextTime });
    if (!nextTime) {
      notifyClear(e);
      return;
    }
    tryEmitCombined(e, nextDate, nextTime);
  };

  const handleDateClear = (
    e: React.SyntheticEvent<HTMLElement>,
    _data: { value: string },
  ) => {
    setDrafts((prev) => ({ ...prev, date: "" }));
    notifyClear(e);
  };

  const handleTimeClear = (
    e: React.SyntheticEvent<HTMLElement>,
    _data: { value: string },
  ) => {
    setDrafts((prev) => ({ ...prev, time: "" }));
    notifyClear(e);
  };

  const resolvedDatePlaceholder = datePlaceholder ?? placeholder ?? "Date";
  const resolvedDateName = dateName ?? name;

  const shared = {
    clearable,
    closable,
    disabled,
    size,
    fluid,
    localization,
    iconPosition,
  };

  return (
    <div
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        ...style,
      }}
    >
      <DateInput
        {...shared}
        name={resolvedDateName}
        placeholder={resolvedDatePlaceholder}
        value={drafts.date}
        dateFormat={dateFormat}
        minDate={minDate}
        maxDate={maxDate}
        disable={disable}
        enable={enable}
        marked={marked}
        markColor={markColor}
        startMode={startMode}
        initialDate={initialDate}
        preserveViewMode={preserveViewMode}
        onChange={handleDateChange}
        onClear={clearable ? handleDateClear : undefined}
        style={{ width: 160, ...dateStyle }}
      />
      <span>at</span>
      <TimeInput
        {...shared}
        name={timeName}
        placeholder={timePlaceholder}
        value={drafts.time}
        timeFormat={timeFormat}
        disableMinute={disableMinute}
        minuteStep={minuteStep}
        onChange={handleTimeChange}
        onClear={clearable ? handleTimeClear : undefined}
        style={{ width: 120, ...timeStyle }}
      />
    </div>
  );
};

export default DateTimeInput;
